import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from './_app';
import Head from 'next/head';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import LeakedPrivatePhotosContent from '@/components/leaked-private-photos';
import { getPageContentContentApi } from '@/lib/service/home';
import { getTdkApi } from '@/lib/service/tdk';
import { faqJsonLd1 } from '@/lib/common/jsonld';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';
import { getFaqList } from '@/utils/landingData';
import { hyphenatedToTitleCase } from '@/shared/path-helper';

// ⭐ ISR: 转换为静态生成 + 7 天重新验证
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale = 'en' } = context;
  const resolvedUrl = '/find-and-remove-revenge-porn';

  const [localeRes, tdkData, res] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl, locale),
    getPageContentContentApi(`/find-and-remove-revenge-porn`, locale, 0, ''),
  ]);

  let currentURL = `${process.env.NEXT_APP_WEB_HOST}/find-and-remove-revenge-porn`;
  if (locale !== 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}/find-and-remove-revenge-porn`;
  }

  if (res.code != 0) {
    return {
      redirect: {
        destination: '/find-and-remove-revenge-porn',
        permanent: false,
      },
    };
  }

  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;
  const pageTitle = tdkData?.data?.title;
  const pageDescription = tdkData?.data?.description;
  const faqList = getFaqList(res);
  const faqJSONLD = faqJsonLd1(
    {
      title: hyphenatedToTitleCase(resolvedUrl),
      desc: pageDescription,
      url: currentURL,
    },
    {
      url: siteUrl,
    },
    faqList,
  );

  return {
    props: {
      ...localeRes,
      herf: currentURL,
      data: res.data,
      title: pageTitle,
      description: pageDescription,
      faqJSONLd: faqJSONLD,
      locale,
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
    },
    revalidate: ISR_REVALIDATE_TIME,
  };
};

const PrivacyPolicyPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: any) => {
  return (
    <>
      <ISRDebugPanel
        generatedAt={props.isrGeneratedAt}
        revalidateIn={props.isrRevalidateIn}
        pageName="OnlyFans Protection"
        additionalInfo={{
          语言: props.locale,
        }}
      />
      <LeakedPrivatePhotosContent data={props.data} />
    </>
  );
};

// eslint-disable-next-line no-undef
PrivacyPolicyPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const description = page?.props?.description;
  const faqJSONLd = page?.props?.faqJSONLd;
  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>

          {/* LCP 优化: 预加载 comparison-section 背景图 (真正的 LCP 元素) */}
          <link
            rel="preload"
            as="image"
            href="https://res-front.pumpsoul.com/prod-erasa/image/erasa-bg-1.webp"
            fetchPriority="high"
          />

          <meta name="title" content={title} />
          <meta name="description" content={description} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />
          <link
            rel="alternate"
            hrefLang="en"
            href={`${process.env.NEXT_APP_WEB_HOST}/en/find-and-remove-revenge-porn`}
          />
          <link
            rel="alternate"
            hrefLang="zh"
            href={`${process.env.NEXT_APP_WEB_HOST}/zh/find-and-remove-revenge-porn`}
          />
          <link
            rel="alternate"
            hrefLang="tw"
            href={`${process.env.NEXT_APP_WEB_HOST}/tw/find-and-remove-revenge-porn`}
          />
          <link
            rel="alternate"
            hrefLang="ja"
            href={`${process.env.NEXT_APP_WEB_HOST}/ja/find-and-remove-revenge-porn`}
          />
          <link
            rel="alternate"
            hrefLang="ko"
            href={`${process.env.NEXT_APP_WEB_HOST}/ko/find-and-remove-revenge-porn`}
          />
          <link
            rel="alternate"
            hrefLang="pt"
            href={`${process.env.NEXT_APP_WEB_HOST}/pt/find-and-remove-revenge-porn`}
          />
          <link
            rel="alternate"
            hrefLang="de"
            href={`${process.env.NEXT_APP_WEB_HOST}/de/find-and-remove-revenge-porn`}
          />
          <link
            rel="alternate"
            hrefLang="es"
            href={`${process.env.NEXT_APP_WEB_HOST}/es/find-and-remove-revenge-porn`}
          />
          <link
            rel="alternate"
            hrefLang="it"
            href={`${process.env.NEXT_APP_WEB_HOST}/it/find-and-remove-revenge-porn`}
          />
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${process.env.NEXT_APP_WEB_HOST}/find-and-remove-revenge-porn`}
          />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          {faqJSONLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJSONLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PrivacyPolicyPage;
